import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, RotateCcw, Send } from 'lucide-react';
import useOpenWorkspace from '../../hooks/useOpenWorkspace';
import { companionApi } from '../../services/companionApi';
import type { CompanionChatMessage, CompanionChatOpportunity } from '../../services/companionApi';

/*
 * "Facing a problem?": a chat with Orbit inside the companion panel, about a
 * problem at work or about the platform. When Orbit finds something worth
 * building it suggests it as a card, and nothing is filed until the person
 * adds it. Continue in Workspace opens a Workspace chat that starts from a
 * brief of this one.
 */

const STARTERS = [
  'Something at work takes too long',
];

const KIND_LABEL: Record<CompanionChatOpportunity['kind'], string> = {
  skill: 'Skill',
  plugin: 'Plugin',
  build: 'Custom build',
};

const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
};

const filedLine = (opp: CompanionChatOpportunity): string => {
  if (opp.filed?.where === 'opportunities') return 'Added to Opportunities.';
  if (opp.filed?.status === 'approved') return 'Added to your company requests.';
  return 'Sent for approval. You can follow it on Projects.';
};

const OpportunityCard: React.FC<{
  opp: CompanionChatOpportunity;
  busy: boolean;
  onDecide: (id: string, action: 'add' | 'dismiss') => void;
  onOpen: (link: string) => void;
  onWorkspace: (id: string) => void;
}> = ({ opp, busy, onDecide, onOpen, onWorkspace }) => (
  <div className="mt-2 rounded-card border border-line bg-white p-3">
    <span className="rounded-full bg-canvas-inset px-2 py-0.5 text-[10.5px] font-semibold text-ink-muted">
      Opportunity · {KIND_LABEL[opp.kind]}
    </span>
    <b className="mt-1.5 block text-[13px] font-semibold leading-[18px] text-ink">{opp.title}</b>
    <p className="m-0 mt-1 text-[12px] leading-[17px] text-ink-muted">{opp.summary}</p>
    {opp.status === 'suggested' && (
      <div className="mt-2.5 flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onDecide(opp.id, 'add')}
          className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-ink/90 disabled:opacity-50"
        >
          Add to Opportunities
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onDecide(opp.id, 'dismiss')}
          className="text-[12px] font-semibold text-ink-muted hover:text-ink disabled:opacity-50"
        >
          Not now
        </button>
      </div>
    )}
    {opp.status === 'added' && (
      <div className="mt-2.5 flex items-center justify-between gap-2 text-[12px]">
        <span className="inline-flex items-center gap-1 font-semibold text-ink">
          <Check size={13} aria-hidden />
          {filedLine(opp)}
        </span>
        {opp.filed?.link && (
          <button
            type="button"
            onClick={() => onOpen(opp.filed?.link || '')}
            className="inline-flex shrink-0 items-center gap-0.5 font-semibold text-ink-muted hover:text-ink"
          >
            Open
            <ArrowRight size={11} aria-hidden />
          </button>
        )}
      </div>
    )}
    {opp.status === 'dismissed' && <small className="mt-2 block text-[11.5px] text-ink-soft">Set aside.</small>}
    {opp.status !== 'dismissed' && (
      <button
        type="button"
        disabled={busy}
        onClick={() => onWorkspace(opp.id)}
        className="mt-2 inline-flex items-center gap-0.5 text-[12px] font-semibold text-ink-muted hover:text-ink disabled:opacity-50"
      >
        Discuss in Workspace
        <ArrowRight size={11} aria-hidden />
      </button>
    )}
  </div>
);

const Bubble: React.FC<{ message: CompanionChatMessage; children?: React.ReactNode }> = ({ message, children }) => {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <p className="m-0 max-w-[85%] whitespace-pre-wrap rounded-card bg-ink px-3 py-2 text-[13px] leading-[19px] text-white">
          {message.text}
        </p>
      </div>
    );
  }
  return (
    <div className="max-w-[92%]">
      <p className="m-0 whitespace-pre-wrap rounded-card bg-canvas-inset px-3 py-2 text-[13px] leading-[19px] text-ink">
        {message.text}
      </p>
      {children}
    </div>
  );
};

export const CompanionChat: React.FC<{
  onBack: () => void;
  onOpen: (link: string) => void;
}> = ({ onBack, onOpen }) => {
  const [messages, setMessages] = useState<CompanionChatMessage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const end = useRef<HTMLDivElement | null>(null);
  const { opening, open } = useOpenWorkspace();

  useEffect(() => {
    companionApi.chat()
      .then(({ data }) => setMessages(data.messages))
      .catch((err) => setError(errorText(err, 'Could not load your chat.')))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [messages, busy]);

  const send = async (text: string) => {
    const said = text.trim();
    if (!said || busy) return;
    setBusy(true);
    setError('');
    setDraft('');
    setMessages((prev) => [...prev, { role: 'user', text: said, at: new Date().toISOString() }]);
    try {
      const { data } = await companionApi.say(said);
      setMessages(data.messages);
    } catch (err) {
      setMessages((prev) => prev.slice(0, -1));
      setDraft(said);
      setError(errorText(err, 'Orbit could not answer just now. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (id: string, action: 'add' | 'dismiss') => {
    setBusy(true);
    setError('');
    try {
      const { data } = await companionApi.decide(id, action);
      setMessages(data.messages);
    } catch (err) {
      setError(errorText(err, 'Could not save that. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const restart = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await companionApi.restart();
      setMessages(data.messages);
    } catch (err) {
      setError(errorText(err, 'Could not start over. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const toWorkspace = (opportunityId?: string) => {
    open(localStorage.getItem('client_id'), async () => (await companionApi.toWorkspace(opportunityId)).data.path);
  };

  const hasAsked = messages.some((m) => m.role === 'user');
  let workspaceLabel = 'Continue in Workspace';
  if (opening) workspaceLabel = 'Opening…';

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    send(draft);
  };

  return (
    <>
      <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink-muted hover:text-ink"
        >
          <ArrowLeft size={13} aria-hidden />
          All tasks
        </button>
        <span className="flex items-center gap-3">
          {messages.length > 0 && (
            <button
              type="button"
              onClick={restart}
              disabled={busy}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink-muted hover:text-ink disabled:opacity-50"
            >
              <RotateCcw size={12} aria-hidden />
              Start over
            </button>
          )}
          {hasAsked && (
            <button
              type="button"
              onClick={() => toWorkspace()}
              disabled={busy || opening}
              className="inline-flex items-center gap-1 rounded-full bg-ink px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-ink/90 disabled:opacity-50"
            >
              {workspaceLabel}
              <ArrowRight size={11} aria-hidden />
            </button>
          )}
        </span>
      </div>

      <div className="grid min-h-0 flex-1 content-start gap-2.5 overflow-y-auto overscroll-contain px-4 py-3">
        {loaded && messages.length === 0 && (
          <div className="grid gap-2">
            <p className="m-0 text-[13px] leading-[19px] text-ink">
              <b className="block">What are you facing?</b>
              Tell me about a problem at work, or ask how anything here works. If it is worth building, I will suggest it for your Opportunities.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setDraft(s)}
                  className="rounded-full border border-line-strong bg-white px-3 py-1 text-[12px] font-semibold text-ink hover:border-ink"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, n) => (
          <Bubble key={`${m.at}-${n}`} message={m}>
            {m.opportunity && <OpportunityCard opp={m.opportunity} busy={busy || opening} onDecide={decide} onOpen={onOpen} onWorkspace={toWorkspace} />}
          </Bubble>
        ))}
        {busy && messages[messages.length - 1]?.role === 'user' && (
          <p className="m-0 w-fit rounded-card bg-canvas-inset px-3 py-2 text-[12.5px] text-ink-muted">Orbit is thinking…</p>
        )}
        <div ref={end} />
      </div>

      <footer className="shrink-0 border-t border-line px-3 py-2.5">
        {error && <p className="m-0 mb-1.5 text-[12px] text-danger">{error}</p>}
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            maxLength={4000}
            placeholder="Describe the problem, or ask a question"
            aria-label="Message Orbit"
            className="min-h-[40px] flex-1 resize-none rounded-card border border-line-strong bg-white px-3 py-2 text-[13px] leading-[18px] text-ink outline-none focus:border-ink"
          />
          <button
            type="button"
            aria-label="Send"
            onClick={() => send(draft)}
            disabled={!draft.trim() || busy}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink text-white hover:bg-ink/90 disabled:cursor-not-allowed disabled:opacity-35"
          >
            <Send size={14} aria-hidden />
          </button>
        </div>
      </footer>
    </>
  );
};

export default CompanionChat;
